import { memo, useEffect, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
// mantine
import {
    Button,
    Divider,
    Collapse,
    ColorInput,
    NumberInput,
    Switch,
    TextInput,
    Textarea
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// components
import { CopyInput } from '@newo/ui-kit';
// icons
import { Settings2 } from 'lucide-react';
// utils
import type { IConnector, ISettingPreset } from '@newo/api/gateway';
import { useShallow } from 'zustand/react/shallow';
import useSettingsStore from '@/pages/Settings/Settings.store';
import Bubble from '@/pages/Settings/components/Chat/components/Bubble';
import ChatPreview from '@/pages/Settings/components/Chat/components/ChatPreview';
import {
    dictionaryToSettings,
    settingsToDictionary
} from '@/pages/Settings/Settings.utils';

interface ChatFormProps {
    connector: IConnector;
    settingsDescription: Record<string, ISettingPreset>;
    schema: Record<string, unknown>;
}

const ChatForm = ({
    connector,
    settingsDescription,
    schema
}: ChatFormProps) => {
    const [isOpened, handlers] = useDisclosure(false);

    const { setChatValues, setChatErrors } = useSettingsStore(
        useShallow((state) => ({
            setChatValues: state.setChatValues,
            setChatErrors: state.setChatErrors
        }))
    );

    const pendingSettings = useSettingsStore(
        (state) => state.chatValues[connector.id]
    );

    const settings = useMemo<Record<string, string>>(() => {
        return settingsToDictionary(connector.settings, settingsDescription);
    }, [connector]);

    const formik = useFormik({
        initialValues: {
            ...settings,
            ...settingsToDictionary(pendingSettings, settingsDescription)
        },
        validationSchema: schema,
        onSubmit: () => {
            /** keep empty */
        },
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true
    });

    const handleToggle = () => {
        handlers.toggle();
    };

    const handleValueChange = useCallback(() => {
        if (formik.isValid && formik.dirty) {
            setChatValues({
                [connector.id]: dictionaryToSettings(formik.values)
            });
        }
    }, [
        formik.isValid,
        formik.dirty,
        formik.values,
        connector.id,
        setChatValues
    ]);

    const handleErrorChange = useCallback(() => {
        setChatErrors({
            [connector.id]: formik.isValid && formik.dirty
        });
    }, [formik.errors.value, connector.id, setChatErrors]);

    useEffect(() => {
        handleValueChange();
    }, [handleValueChange]);

    useEffect(() => {
        handleErrorChange();
    }, [handleErrorChange]);

    return (
        <article>
            <div className="font-semibold mb-1">{connector.connector_idn}</div>
            <div className="flex text-sm flex-col gap-4 relative rounded-lg bg-[#FAFAFA] p-4 border-1 border-solid border-[#DEE2E6]/50">
                <CopyInput
                    description={
                        settingsDescription.widget_script_url.description
                    }
                    label={settingsDescription.widget_script_url.title}
                    value={settings.widget_script_url}
                />

                <Button
                    className="self-start pl-0"
                    leftSection={<Settings2 />}
                    size="xs"
                    variant="transparent"
                    onClick={handleToggle}
                >
                    {isOpened ? 'Hide settings' : 'Show settings'}
                </Button>
                <Collapse in={isOpened}>
                    <div className="flex flex-col gap-4">
                        <TextInput
                            withAsterisk
                            description={
                                settingsDescription.client_website_url
                                    .description
                            }
                            error={formik.errors.client_website_url}
                            label={settingsDescription.client_website_url.title}
                            name="client_website_url"
                            placeholder="https://example.com"
                            value={formik.values.client_website_url}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                        />

                        <CopyInput
                            description={
                                settingsDescription.client_secret.description
                            }
                            label={settingsDescription.client_secret.title}
                            name="client_secret"
                            value={settings.client_secret}
                        />
                        <NumberInput
                            description={
                                settingsDescription.auto_open_after.description
                            }
                            error={formik.errors.auto_open_after}
                            label={settingsDescription.auto_open_after.title}
                            name="auto_open_after"
                            value={
                                formik.values.auto_open_after ||
                                (settingsDescription.auto_open_after
                                    .default_value as string)
                            }
                            onBlur={formik.handleBlur}
                            onChange={(value) => {
                                formik.setFieldValue(
                                    'auto_open_after',
                                    Number(value)
                                );
                            }}
                        />

                        <Switch
                            defaultChecked={
                                formik.values.showcase_enabled === 'enable'
                            }
                            label="Enable showcase"
                            onChange={({ currentTarget }) => {
                                formik.setFieldValue(
                                    'showcase_enabled',
                                    currentTarget.checked ? 'enable' : 'disable'
                                );
                            }}
                        />
                        <Divider className="my-4" />

                        <div className="grid grid-cols-1 md:grid-cols-[1fr_20rem] gap-8">
                            <div className="flex flex-col gap-4">
                                <TextInput
                                    description={
                                        settingsDescription.agent_name
                                            .description
                                    }
                                    error={formik.errors.agent_name}
                                    label={settingsDescription.agent_name.title}
                                    name="agent_name"
                                    placeholder="John Doe"
                                    value={formik.values.agent_name}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />
                                <TextInput
                                    description={
                                        settingsDescription.agent_picture_url
                                            .description
                                    }
                                    error={formik.errors.agent_picture_url}
                                    label={
                                        settingsDescription.agent_picture_url
                                            .title
                                    }
                                    name="agent_picture_url"
                                    placeholder="https://example.com/avatar.jpg"
                                    value={formik.values.agent_picture_url}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />

                                <Textarea
                                    description={
                                        settingsDescription.bubble_text
                                            .description
                                    }
                                    error={formik.errors.bubble_text}
                                    label={
                                        settingsDescription.bubble_text.title
                                    }
                                    name="bubble_text"
                                    placeholder="How can I help you today?"
                                    value={formik.values.bubble_text}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />

                                <Textarea
                                    description={
                                        settingsDescription.greeting_phrase
                                            .description
                                    }
                                    error={formik.errors.greeting_phrase}
                                    label={
                                        settingsDescription.greeting_phrase
                                            .title
                                    }
                                    name="greeting_phrase"
                                    placeholder="Hello! Welcome to our support chat. How can I assist you?"
                                    value={formik.values.greeting_phrase}
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                />
                                <ColorInput
                                    description={
                                        settingsDescription.color.description
                                    }
                                    error={formik.errors.color}
                                    label={settingsDescription.color.title}
                                    placeholder="Pick a color"
                                    size="xs"
                                    value={formik.values.color}
                                    onBlur={formik.handleBlur}
                                    onChange={(value) => {
                                        formik.setFieldValue('color', value);
                                    }}
                                />
                                <div className="flex flex-col">
                                    <div className="text-xs font-semibold">
                                        Icon position
                                    </div>
                                    <div className="text-[10px] text-gray-6 mt-0.5 mb-1">
                                        Shift in pixels relative to bottom-right
                                        corner
                                    </div>
                                    <div className="flex gap-2 items-start">
                                        <span className="pt-1">x:</span>
                                        <NumberInput
                                            error={
                                                formik.errors.icon_position_x
                                            }
                                            min={0}
                                            name="icon_position_x"
                                            rightSection={
                                                <span className="pr-1">px</span>
                                            }
                                            value={
                                                formik.values.icon_position_x
                                            }
                                            onBlur={formik.handleBlur}
                                            onChange={(value) => {
                                                formik.setFieldValue(
                                                    'icon_position_x',
                                                    Number(value)
                                                );
                                            }}
                                        />
                                        <span className="pt-1">y:</span>
                                        <NumberInput
                                            error={
                                                formik.errors.icon_position_y
                                            }
                                            min={0}
                                            name="icon_position_y"
                                            rightSection={
                                                <span className="pr-1">px</span>
                                            }
                                            value={
                                                formik.values.icon_position_y
                                            }
                                            onBlur={formik.handleBlur}
                                            onChange={(value) => {
                                                formik.setFieldValue(
                                                    'icon_position_y',
                                                    Number(value)
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-10">
                                <ChatPreview
                                    color={formik.values.color}
                                    greetingPhrase={
                                        formik.values.greeting_phrase
                                    }
                                    name={formik.values.agent_name}
                                    pictureUrl={formik.values.agent_picture_url}
                                />

                                <Divider />

                                <Bubble
                                    bubbleText={formik.values.bubble_text}
                                    color={formik.values.color}
                                    pictureUrl={formik.values.agent_picture_url}
                                />
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
        </article>
    );
};

export default memo(ChatForm);
